import { Fragment, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { AnalyticsUtils } from '@/configuration/setup/googleAnalytics';
import { getSoHStatusClass, LOW_MILEAGE, NO_DATA } from '@/features/batteryOverview/utils/BatteryDataUtils';
import { BatteryPacksResponse } from '@/generated/GeneratedApiTypes';
import SoHValue from './SoHValue';

const BATTERY_SERVICE_DIALOGS = 'BATTERY-SERVICE-DIALOGS';

const getStatusColumnSize = (status: string | undefined) => {
    if (status === NO_DATA || status === LOW_MILEAGE) return '3';
    else return '4';
};

export const PackLevelOverview = ({ batteryPackResponse }: { batteryPackResponse: BatteryPacksResponse }) => {
    const { sendMessageToParent } = usePostMessageSender();
    const navigate = useNavigate();
    const { asset_id: assetId, packs } = batteryPackResponse;

    const handleCloseDialog = useCallback(() => {
        sendMessageToParent({
            type: `${BATTERY_SERVICE_DIALOGS}${CLOSE_DIALOG_EVENT}`,
            payload: 'Closed Pack Level dialog',
        });
    }, [sendMessageToParent]);

    const handlePackDetailsView = useCallback(
        (packId: string) => {
            AnalyticsUtils.gaPush({
                event: 'button_pack_arrow',
                parameters: {
                    element_name: 'PackDetailViewButton',
                    component_name: 'Button',
                    flow_name: 'view_workshop_battery_health_data',
                    trigger: 'click',
                },
                userProps: {},
            });
            navigate(`/dialog/pack-levels/${assetId}/${packId}`);
        },
        [navigate, assetId],
    );

    return (
        <>
            <div className="display-flex justify-content-between align-items-center padding-10 border-style-solid border-bottom-only border-width-1 border-color-light">
                <span className="text-size-22">
                    <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.details" />
                </span>
                <button
                    type="button"
                    className="btn btn-icon-only btn-muted close"
                    onClick={handleCloseDialog}
                    aria-label="dialog close button"
                >
                    <span className="rioglyph rioglyph-remove" aria-hidden="true" />
                </button>
            </div>
            <Dialog.Body className={'padding-15'}>
                {packs.map(({ id, state_of_health_percentage, state_of_health_status }) => (
                    <Fragment key={id}>
                        <div className="margin-bottom-10 text-size-14">
                            <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.pack" /> &nbsp;
                            {id}
                        </div>
                        <div className="list-group border margin-bottom-15 padding-left-10">
                            <div
                                className={`list-group-item list-group-item-${getSoHStatusClass(state_of_health_status)} rounded-none display-flex row text-size-12 align-items-center`}
                            >
                                <div
                                    className={`text-color-darkest col-${getStatusColumnSize(state_of_health_status)}`}
                                >
                                    <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.packSoH" />
                                </div>
                                <SoHValue
                                    status={state_of_health_status ?? ''}
                                    percentage={state_of_health_percentage}
                                    textSize={12}
                                    valueClassName="text-color-darkest"
                                />
                                <div className="margin-left-auto padding-left-10">
                                    <button
                                        type="button"
                                        className="btn btn-link text-color-black padding-0"
                                        onClick={() => handlePackDetailsView(id!)}
                                        aria-label={`View details for pack ${id}`}
                                    >
                                        <span className="rioglyph rioglyph-chevron-right" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                ))}
                <div className="border-style-solid border-top-only border-width-1 border-color-light margin-left--15 margin-right--15" />
            </Dialog.Body>
        </>
    );
};
